// pkg72a — green→navy conversion: decorative dark-green surfaces (#1a3a2a/#2a5a4a — banner gradients, featured borders, buttons, toggles, tabs) become NAVY_DEEP #0F1838 → NAVY #1E2761; SEMANTIC greens kept (status/success badges, category chip triads, ✓ text); selected-module card converts as a pair (navy border + pale-navy #F0F2F8 tint) — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
// pkg44 — awaiting-attorney handling: the review-and-sign step is offered only when the attorney's fields are complete; status refetched after submit; onBack refetches instead of assuming acknowledged — delivered 2026-07-04
// pkg43 — clients see only client-owned fields (ownerFilter); attorney-prepared modules show a preparing placeholder, then go straight to review/acknowledge — delivered 2026-07-04
// pkg41 — acknowledgment step 2: after submit, modules with the toggle route into AcknowledgmentView (review the rendered document, sign with typed name); pending/superseded banner on return visits — delivered 2026-07-04
// pkg36 — assembly modules render the step-by-step ModuleWizard (save-as-you-go); flat modules keep the original single-page form — delivered 2026-07-03
import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useClientModules } from '../contexts/ClientModulesContext';
import { api } from '../utils/api';
import ModuleWizard from '../components/ModuleWizard';
import AcknowledgmentView from '../components/AcknowledgmentView'; // pkg41
import { moduleCompletedBy, hasOwnedFields } from '../utils/fieldOwnership'; // pkg43

// Renders a dynamic form from a firm_modules schema_json.
// Field types: text | multiline | date | yes_no | dropdown
// On submit, POSTs answers to /api/firm-modules/me/:id/submit.
// Clients can re-submit to update their answers at any time.

function FieldInput({ field, value, onChange }) {
  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '8px 11px', fontSize: 14,
    fontFamily: 'Arial, sans-serif',
    border: '1px solid #ccc', borderRadius: 4,
  };

  switch (field.type) {
    case 'multiline':
      return (
        <textarea
          rows={4}
          value={value ?? ''}
          placeholder={field.placeholder || ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      );

    case 'date':
      return (
        <input
          type="date"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 220 }}
        />
      );

    case 'yes_no':
      return (
        <div style={{ display: 'flex', gap: 20, marginTop: 2 }}>
          {['yes', 'no'].map(opt => (
            <label key={opt} style={{ fontFamily: 'Arial, sans-serif', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              {opt === 'yes' ? 'Yes' : 'No'}
            </label>
          ))}
        </div>
      );

    case 'dropdown':
      return (
        <select
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 320 }}
        >
          <option value="">— Select —</option>
          {(field.options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );

    default: // 'text'
      return (
        <input
          type="text"
          value={value ?? ''}
          placeholder={field.placeholder || ''}
          onChange={e => onChange(e.target.value)}
          style={{ ...inputStyle, maxWidth: 480 }}
        />
      );
  }
}

export default function AiModulePage() {
  const { id } = useParams();
  const { firmModules, loading: modulesLoading } = useClientModules();

  const [module,    setModule]    = useState(null);
  const [answers,   setAnswers]   = useState({});
  const [loading,   setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitErr,  setSubmitErr]  = useState('');
  const [validationErrs, setValidationErrs] = useState({});

  // pkg41 — acknowledgment state. ackStatus: null (n/a or loading) |
  // 'none' | 'pending' | 'acknowledged' | 'superseded'. showAck flips the
  // page into the review-and-sign view.
  const [ackStatus, setAckStatus] = useState(null);
  const [ackInfo,   setAckInfo]   = useState(null); // pkg43 — { acknowledgment_id, acknowledged_at, ... }
  const [showAck,   setShowAck]   = useState(false);

  // Fetch the full module (with schema + prior answers if any).
  useEffect(() => {
    if (modulesLoading) return;
    // Quick check: is this id in the firm modules list?
    const listed = firmModules.find(m => m.id === id);
    if (!listed) { setLoading(false); return; }

    api.getMyFirmModule(id)
      .then(data => {
        setModule(data);
        // Pre-fill from prior submission if it exists.
        if (data.answers && typeof data.answers === 'object') {
          setAnswers(data.answers);
        }
        setLoading(false);
        // pkg41 — modules with the toggle: learn where the client stands so
        // return visits can prompt "review & acknowledge" without re-submitting.
        if (data.requires_acknowledgment && data.kind === 'assembly') {
          api.getMyAcknowledgment(id)
            .then(a => { setAckStatus(a.status || 'none'); setAckInfo(a); }) // pkg43 — keep the id for the client's copy download
            .catch(() => {}); // banner is a nicety — never block the form
        }
      })
      .catch(() => setLoading(false));
  }, [id, modulesLoading, firmModules]);

  // pkg44 — ack status must be fresh right after a submit (the gate can flip).
  function refreshAckStatus() {
    if (!module?.requires_acknowledgment || module?.kind !== 'assembly') return;
    api.getMyAcknowledgment(id)
      .then(a => { setAckStatus(a.status || 'none'); setAckInfo(a); })
      .catch(() => {});
  }

  function setAnswer(fieldId, value) {
    setAnswers(prev => ({ ...prev, [fieldId]: value }));
    // Clear validation error for this field on change.
    if (validationErrs[fieldId]) {
      setValidationErrs(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
    }
  }

  function validate() {
    const errs = {};
    for (const section of (module?.schema_json?.sections || [])) {
      for (const field of (section.fields || [])) {
        if (!field.required) continue;
        const val = answers[field.id];
        if (val === undefined || val === null || val === '') {
          errs[field.id] = 'This field is required.';
        }
      }
    }
    return errs;
  }

  async function handleSubmit() {
    const errs = validate();
    if (Object.keys(errs).length) {
      setValidationErrs(errs);
      // Scroll to first error
      const firstId = Object.keys(errs)[0];
      document.getElementById(`field-${firstId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true); setSubmitErr('');
    try {
      await api.submitFirmModule(id, answers);
      setSubmitted(true);
      refreshAckStatus(); // pkg44
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitErr(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading states ──────────────────────────────────────────────────────
  if (loading || modulesLoading) {
    return (
      <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>
        Loading…
      </div>
    );
  }

  if (!module) {
    return <Navigate to="/" replace />;
  }

  const schema = module.schema_json || { sections: [] };

  // pkg36 — assembly modules use the wizard; answers auto-save between steps.
  const isAssembly = module.kind === 'assembly';

  async function handleWizardSubmit() {
    setSubmitting(true); setSubmitErr('');
    try {
      await api.submitFirmModule(id, answers);
      setSubmitted(true);
      refreshAckStatus(); // pkg44
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitErr(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Acknowledgment view (pkg41) ─────────────────────────────────────────
  const needsAck = module.requires_acknowledgment === true && isAssembly;

  // pkg43 — attorney-prepared modules: the client fills nothing. Either the
  // module is attorney-completed outright, or review-screen edits left it
  // with zero client-owned fields. The client's page shows a "being
  // prepared" placeholder until the attorney finalizes, then goes straight
  // to review-and-acknowledge (toggle on) or a simple prepared notice.
  const attorneyPrepared = isAssembly &&
    (moduleCompletedBy(schema) === 'attorney' || !hasOwnedFields(schema, 'client'));

  if (attorneyPrepared) {
    const prepared = module.submission_status === 'submitted';
    const infoBox = (bg, border, color, title, body, extra) => (
      <div>
        <h1 className="page-title">{module.label}</h1>
        {module.description && <p className="page-subtitle">{module.description}</p>}
        <div style={{
          background: bg, border: `1px solid ${border}`, borderRadius: 6,
          padding: '20px 24px', maxWidth: 560, fontFamily: 'Arial, sans-serif',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color }}>{title}</div>
          <div style={{ fontSize: 14, color: '#444' }}>{body}</div>
          {extra}
        </div>
      </div>
    );

    if (!prepared) {
      return infoBox('#f8fafb', '#dde3ec', '#0F1838',
        'Your attorney is preparing this document',
        "There's nothing for you to fill out. You'll be able to review it here once it's ready.");
    }
    if (needsAck && ackStatus === null) {
      // Status still loading — don't open the acknowledgment view yet (it
      // would create a pending snapshot even for already-signed documents).
      return infoBox('#f8fafb', '#dde3ec', '#0F1838', 'Loading…', 'One moment.');
    }
    if (needsAck && ackStatus !== 'acknowledged') {
      // Prepared and awaiting the client's signature — straight to the view.
      return (
        <div>
          <h1 className="page-title">{module.label}</h1>
          <AcknowledgmentView
            moduleId={id}
            moduleLabel={module.label}
            onBack={() => {
              setAckStatus('acknowledged');
              api.getMyAcknowledgment(id).then(a => setAckInfo(a)).catch(() => {});
            }}
          />
        </div>
      );
    }
    if (needsAck && ackStatus === 'acknowledged') {
      return infoBox('#f0faf5', '#4caf7d', '#1a3a2a',
        '✓ Document acknowledged',
        `You reviewed and signed this document${ackInfo?.acknowledged_at ? ` on ${new Date(ackInfo.acknowledged_at).toLocaleDateString()}` : ''}. Your attorney has a copy of exactly what you signed.`,
        ackInfo?.acknowledgment_id ? (
          <button
            onClick={async () => {
              try {
                const blob = await api.downloadMyAcknowledgedCopy(id, ackInfo.acknowledgment_id);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'acknowledged-document.docx';
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
              } catch (_) { /* download is a nicety here */ }
            }}
            style={{ marginTop: 14, fontSize: 13, fontFamily: 'Arial, sans-serif', padding: '8px 16px', borderRadius: 4, border: 'none', background: '#0F1838', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
          >
            ↓ Download your acknowledged copy
          </button>
        ) : null);
    }
    return infoBox('#f0faf5', '#4caf7d', '#1a3a2a',
      'Document prepared',
      'Your attorney has prepared this document. No action is needed from you.');
  }

  if (showAck && needsAck) {
    return (
      <div>
        <h1 className="page-title">{module.label}</h1>
        <AcknowledgmentView
          moduleId={id}
          moduleLabel={module.label}
          onBack={() => { setShowAck(false); setSubmitted(false); refreshAckStatus(); }} /* pkg44 */
        />
      </div>
    );
  }

  // ── Success state ───────────────────────────────────────────────────────
  if (submitted) {
    // pkg41 — step 2: modules with the acknowledgment toggle route the client
    // into the review-and-sign view instead of ending at "submitted".
    if (needsAck) {
      return (
        <div>
          <h1 className="page-title">{module.label}</h1>
          <div style={{
            background: '#f0faf5', border: '1px solid #4caf7d',
            borderRadius: 6, padding: '20px 24px', maxWidth: 560,
            fontFamily: 'Arial, sans-serif',
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#0F1838' }}>
              Step 1 complete — answers submitted
            </div>
            <div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>
              {ackStatus === 'awaiting_attorney' /* pkg44 */
                ? 'Your attorney is completing their portion of the document. Once it\u2019s ready, you\u2019ll review and sign it here \u2014 nothing more to do right now.'
                : 'One more step: your attorney needs you to review the completed document and acknowledge it.'}
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {ackStatus !== 'awaiting_attorney' && ( /* pkg44 */
              <button
                onClick={() => setShowAck(true)}
                style={{
                  fontSize: 14, fontFamily: 'Arial, sans-serif',
                  padding: '10px 24px', borderRadius: 4, border: 'none',
                  background: '#0F1838', color: '#fff', cursor: 'pointer', fontWeight: 600,
                }}
              >
                Review & acknowledge the document →
              </button>
              )}
              <button
                onClick={() => setSubmitted(false)}
                style={{
                  fontSize: 13, fontFamily: 'Arial, sans-serif',
                  padding: '10px 16px', borderRadius: 4, border: 'none',
                  background: '#eef2f7', color: '#0F1838', cursor: 'pointer', fontWeight: 600,
                }}
              >
                Edit my responses
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div>
        <h1 className="page-title">{module.label}</h1>
        <div style={{
          background: '#f0faf5', border: '1px solid #4caf7d',
          borderRadius: 6, padding: '20px 24px', maxWidth: 540,
          fontFamily: 'Arial, sans-serif',
        }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: '#0F1838' }}>
            Submitted successfully
          </div>
          <div style={{ fontSize: 14, color: '#444' }}>
            Your responses have been sent to your attorney. You can return to this page to update your answers at any time.
          </div>
          <button
            onClick={() => setSubmitted(false)}
            style={{
              marginTop: 16, fontSize: 13, fontFamily: 'Arial, sans-serif',
              padding: '7px 16px', borderRadius: 4, border: 'none',
              background: '#0F1838', color: '#fff', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Edit my responses
          </button>
        </div>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────
  const hasExistingSubmission = !!module.submission_id;

  return (
    <div>
      <h1 className="page-title">{module.label}</h1>

      {module.description && (
        <p className="page-subtitle">{module.description}</p>
      )}

      {hasExistingSubmission && (
        <div style={{
          background: '#fff8e1', border: '1px solid #f5c842',
          borderRadius: 4, padding: '10px 14px', marginBottom: 20,
          fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#7c5200',
        }}>
          You have already submitted this form. You can update your answers below and re-submit at any time.
        </div>
      )}

      {/* pkg41 — outstanding acknowledgment on a return visit: the client
          submitted (or re-submitted) but hasn't signed the current version. */}
      {hasExistingSubmission && needsAck && ackStatus === 'awaiting_attorney' && ( /* pkg44 */
        <div style={{
          background: '#f8fafb', border: '1px solid #dde3ec',
          borderRadius: 4, padding: '12px 14px', marginBottom: 20,
          fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555',
        }}>
          Your answers are in. Your attorney is completing their portion of the document — you'll review and sign it here once it's ready.
        </div>
      )}

      {hasExistingSubmission && needsAck && ['none', 'pending', 'superseded'].includes(ackStatus) && (
        <div style={{
          background: '#eef4ff', border: '1px solid #9db8e8',
          borderRadius: 4, padding: '12px 14px', marginBottom: 20,
          fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#1a3a5c',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 220 }}>
            {ackStatus === 'superseded'
              ? 'You updated your answers after signing — the current version of your document still needs your acknowledgment.'
              : 'Your attorney needs you to review the completed document and acknowledge it.'}
          </span>
          <button
            onClick={() => setShowAck(true)}
            style={{
              fontSize: 13, fontFamily: 'Arial, sans-serif',
              padding: '7px 16px', borderRadius: 4, border: 'none',
              background: '#0F1838', color: '#fff', cursor: 'pointer', fontWeight: 600,
            }}
          >
            Review & acknowledge →
          </button>
        </div>
      )}

      {submitErr && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 4, padding: '10px 14px', marginBottom: 16,
          fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c',
        }}>
          {submitErr}
        </div>
      )}

      {isAssembly ? ( /* pkg36 — one step per screen, saves as you go */
        <ModuleWizard
          schema={schema}
          answers={answers}
          onChange={setAnswer}
          onStepSave={() => api.saveFirmModuleDraft(id, answers)}
          onSubmit={handleWizardSubmit}
          submitting={submitting}
          ownerFilter="client"  /* pkg43 — clients see only their own fields */
        />
      ) : (<>
      {(schema.sections || []).map(section => (
        <div key={section.id} className="card" style={{ marginBottom: 20 }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif', fontSize: 16, fontWeight: 700,
            color: '#0F1838', marginTop: 0, marginBottom: 16,
            paddingBottom: 10, borderBottom: '1px solid #eee',
          }}>
            {section.title}
          </h2>

          {(section.fields || []).map(field => (
            <div
              key={field.id}
              id={`field-${field.id}`}
              style={{ marginBottom: 18 }}
            >
              <label style={{
                fontFamily: 'Arial, sans-serif', fontSize: 14, fontWeight: 600,
                color: '#222', display: 'block', marginBottom: 4,
              }}>
                {field.label}
                {field.required && (
                  <span style={{ color: '#B85042', marginLeft: 3 }}>*</span>
                )}
              </label>

              {field.instructions && (
                <div style={{
                  fontFamily: 'Arial, sans-serif', fontSize: 12,
                  color: '#666', marginBottom: 5,
                }}>
                  {field.instructions}
                </div>
              )}

              <FieldInput
                field={field}
                value={answers[field.id]}
                onChange={val => setAnswer(field.id, val)}
              />

              {validationErrs[field.id] && (
                <div style={{
                  fontFamily: 'Arial, sans-serif', fontSize: 12,
                  color: '#B85042', marginTop: 4,
                }}>
                  {validationErrs[field.id]}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      {Object.keys(validationErrs).length > 0 && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          borderRadius: 4, padding: '10px 14px', marginBottom: 16,
          fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#b91c1c',
        }}>
          Please fill in all required fields before submitting.
        </div>
      )}

      <div style={{ marginTop: 8, marginBottom: 40 }}>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            fontSize: 14, fontFamily: 'Arial, sans-serif',
            padding: '10px 28px', borderRadius: 4, border: 'none',
            background: '#0F1838', color: '#fff',
            cursor: submitting ? 'default' : 'pointer', fontWeight: 600,
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Submitting…' : hasExistingSubmission ? 'Update my responses' : 'Submit'}
        </button>
      </div>
      </>)}
    </div>
  );
}
